import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Handshake, Search, Loader2, Download, Plus, Sparkles, Copy, X, Trash2, Mail, Phone, MapPin, RefreshCw, Send, ChevronDown, Zap, ExternalLink, Bookmark, Building2, Users, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import PageTour from "@/components/PageTour";

const STATUSES = ["new", "contacted", "responded", "interested", "rejected"];
const statusTone = {
  new:        "bg-slate-100 text-slate-700 border-slate-200",
  contacted:  "bg-sky-100 text-sky-700 border-sky-200",
  responded:  "bg-violet-100 text-violet-700 border-violet-200",
  interested: "bg-emerald-100 text-emerald-700 border-emerald-200",
  rejected:   "bg-rose-100 text-rose-700 border-rose-200",
};
const nudgeTone = {
  scheduled:       "bg-slate-100 text-slate-700 border-slate-200",
  sent:            "bg-emerald-100 text-emerald-700 border-emerald-200",
  pending_manual:  "bg-amber-100 text-amber-700 border-amber-200",
  skipped:         "bg-slate-100 text-slate-500 border-slate-200",
};

function money(v) {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function ContactRow({ c, onStatus, onDelete, onEnrich, onDraft }) {
  const [expanded, setExpanded] = useState(false);
  const [nudges, setNudges] = useState([]);
  const [loadingNudges, setLoadingNudges] = useState(false);
  const [busyNudge, setBusyNudge] = useState(null);
  const [enriching, setEnriching] = useState(false);
  const [editingNudge, setEditingNudge] = useState(null);

  const loadNudges = async () => {
    setLoadingNudges(true);
    try {
      const r = await api.get(`/connect/nudges?contact_id=${c.contact_id}`);
      setNudges(r.data || []);
    } catch (err) {
      console.error("Load nudges failed:", err);
    }
    finally { setLoadingNudges(false); }
  };
  // Any time this row's status changes to "contacted", refresh the cadence — the backend
  // will have auto-armed the 3 follow-ups on the PATCH.
  useEffect(() => {
    if (c.status === "contacted") {
      loadNudges();
      setExpanded(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c.status]);
  const toggle = async () => {
    if (!expanded) await loadNudges();
    setExpanded(!expanded);
  };
  const enrichNow = async () => {
    setEnriching(true);
    try {
      const r = await api.post(`/connect/contacts/${c.contact_id}/enrich`);
      onEnrich(r.data);
      toast.success("Enriched from SAM.gov");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "SAM.gov returned no data for this UEI");
    } finally { setEnriching(false); }
  };
  const startCadence = async () => {
    try {
      const r = await api.post(`/connect/nudges/${c.contact_id}/generate`);
      setNudges(r.data.nudges || []);
      setExpanded(true);
      toast.success(`Cadence armed — ${r.data.nudges.length} follow-ups scheduled`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create cadence"); }
  };
  const draftNudge = async (n) => {
    setBusyNudge(n.nudge_id);
    try {
      const r = await api.post(`/connect/nudges/${n.nudge_id}/draft`);
      setNudges(list => list.map(x => x.nudge_id === n.nudge_id ? { ...x, subject: r.data.subject, body: r.data.body } : x));
      setEditingNudge(n.nudge_id);
    } catch (e) { toast.error(e?.response?.data?.detail || "AI draft failed"); }
    finally { setBusyNudge(null); }
  };
  const sendNudge = async (n) => {
    if (!n.subject || !n.body) { toast.error("Draft the email first"); return; }
    setBusyNudge(n.nudge_id);
    try {
      const r = await api.post(`/connect/nudges/${n.nudge_id}/send`);
      if (r.data.sent) {
        setNudges(list => list.map(x => x.nudge_id === n.nudge_id ? { ...x, status: "sent" } : x));
        toast.success("Nudge sent");
      } else if (r.data.mailto) {
        window.location.href = r.data.mailto;
        setNudges(list => list.map(x => x.nudge_id === n.nudge_id ? { ...x, status: "pending_manual" } : x));
        toast.info("Opened in your mail app — mark it sent once delivered.");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setBusyNudge(null); }
  };
  const saveNudge = async (n, patch) => {
    try {
      await api.patch(`/connect/nudges/${n.nudge_id}`, patch);
      setNudges(list => list.map(x => x.nudge_id === n.nudge_id ? { ...x, ...patch } : x));
    } catch (e) { toast.error("Save failed"); }
  };
  const cancelNudge = async (n) => {
    if (!window.confirm(`Cancel ${n.label}?`)) return;
    try {
      await api.delete(`/connect/nudges/${n.nudge_id}`);
      setNudges(list => list.filter(x => x.nudge_id !== n.nudge_id));
    } catch (err) {
      console.error("Delete nudge failed:", err);
      toast.error("Could not remove nudge");
    }
  };

  const hasPOC = c.poc_email || c.poc_phone || c.poc_name;

  return (
    <div className="border-b border-slate-100 last:border-b-0" data-testid={`contact-row-${c.contact_id}`}>
      <div className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <div className="font-semibold text-slate-900">{c.prime_name}</div>
              <span className={`text-[10px] font-mono font-bold tracking-widest px-2 py-0.5 rounded-full border ${statusTone[c.status] || statusTone.new}`}>{(c.status || "new").toUpperCase()}</span>
              {c.sam_enriched_at && <span className="text-[10px] font-mono font-bold tracking-widest px-2 py-0.5 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200">SAM ENRICHED</span>}
            </div>
            <div className="text-[11px] font-mono text-slate-500">{c.agency || "—"}{c.naics ? ` · NAICS ${c.naics}` : ""}{c.award_amount ? ` · ${money(c.award_amount)}` : ""}</div>
            {hasPOC && (
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-700" data-testid={`poc-${c.contact_id}`}>
                {c.poc_name && <span className="font-semibold">{c.poc_name}{c.poc_title ? `, ${c.poc_title}` : ""}</span>}
                {c.poc_email && <a href={`mailto:${c.poc_email}`} className="inline-flex items-center gap-1 text-violet-600 hover:text-violet-700"><Mail className="w-3.5 h-3.5" />{c.poc_email}</a>}
                {c.poc_phone && <a href={`tel:${c.poc_phone.replace(/[^\d+]/g, "")}`} className="inline-flex items-center gap-1 text-emerald-600 hover:text-emerald-700"><Phone className="w-3.5 h-3.5" />{c.poc_phone}</a>}
                {c.address && <span className="inline-flex items-center gap-1 text-slate-500"><MapPin className="w-3.5 h-3.5" />{c.address}</span>}
                {c.website && <a href={c.website.startsWith("http") ? c.website : `https://${c.website}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sky-600 hover:text-sky-700"><ExternalLink className="w-3.5 h-3.5" />{c.website}</a>}
              </div>
            )}
            {c.award_description && <div className="text-sm text-slate-600 mt-2 line-clamp-2">{c.award_description}</div>}
            {c.notes && <div className="text-sm text-slate-500 mt-2 italic">"{c.notes}"</div>}
          </div>
          <div className="flex flex-col items-end gap-2 shrink-0">
            <select value={c.status || "new"} onChange={(e) => onStatus(c, e.target.value)} className="text-xs rounded-full bg-white border border-slate-200 focus:border-violet-500 px-3 py-1 outline-none font-mono font-bold tracking-widest uppercase" data-testid={`status-${c.contact_id}`}>
              {STATUSES.map(s => <option key={s} value={s}>{s.toUpperCase()}</option>)}
            </select>
            <div className="flex items-center gap-3 flex-wrap justify-end">
              {c.prime_uei && (
                <button onClick={enrichNow} disabled={enriching} className="text-[10px] font-mono font-bold tracking-widest text-emerald-600 hover:text-emerald-700 inline-flex items-center gap-1 disabled:opacity-50" data-testid={`enrich-${c.contact_id}`} title="Pull POC from SAM.gov Entity API">
                  {enriching ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}ENRICH
                </button>
              )}
              <button onClick={() => onDraft({ prime_name: c.prime_name, agency: c.agency, award_description: c.award_description, naics: c.naics })} className="text-[10px] font-mono font-bold tracking-widest text-fuchsia-600 hover:text-fuchsia-700 inline-flex items-center gap-1" data-testid={`contact-draft-${c.contact_id}`}><Mail className="w-3 h-3" />INTRO</button>
              <button onClick={toggle} className="text-[10px] font-mono font-bold tracking-widest text-violet-600 hover:text-violet-700 inline-flex items-center gap-1" data-testid={`cadence-toggle-${c.contact_id}`}>
                <Zap className="w-3 h-3" />CADENCE <ChevronDown className={`w-3 h-3 transition-transform ${expanded ? "rotate-180" : ""}`} />
              </button>
              <button onClick={() => onDelete(c)} className="text-[10px] font-mono font-bold tracking-widest text-rose-500 hover:text-rose-700 inline-flex items-center gap-1" data-testid={`contact-delete-${c.contact_id}`}><Trash2 className="w-3 h-3" />REMOVE</button>
            </div>
          </div>
        </div>
      </div>

      {expanded && (
        <div className="bg-violet-50/40 border-t border-slate-100 p-5" data-testid={`cadence-${c.contact_id}`}>
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <div>
              <div className="text-[10px] font-mono font-bold tracking-widest text-violet-600">3-TOUCH FOLLOW-UP CADENCE</div>
              <div className="text-xs text-slate-500 mt-1">Auto-armed when you set status to "contacted". +3 / +7 / +14 days.</div>
            </div>
            {nudges.length === 0 && !loadingNudges && (
              <button onClick={startCadence} className="bf-pill px-4 py-2 text-[10px] tracking-widest uppercase font-display font-bold inline-flex items-center gap-2" data-testid={`cadence-arm-${c.contact_id}`}>
                <Zap className="w-3.5 h-3.5" />ARM CADENCE
              </button>
            )}
          </div>
          {loadingNudges && <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>}
          {!loadingNudges && nudges.length === 0 && (
            <div className="text-sm text-slate-500">No follow-ups scheduled yet.</div>
          )}
          <div className="space-y-2">
            {nudges.map((n) => (
              <div key={n.nudge_id} className="rounded-xl bg-white border border-slate-200 p-4" data-testid={`nudge-${n.nudge_id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-semibold text-slate-900">{n.label}</span>
                      <span className={`text-[10px] font-mono font-bold tracking-widest px-2 py-0.5 rounded-full border ${nudgeTone[n.status] || nudgeTone.scheduled}`}>{(n.status || "scheduled").replace("_", " ").toUpperCase()}</span>
                      <span className="text-[11px] font-mono text-slate-500">Send {new Date(n.scheduled_at).toLocaleDateString()}</span>
                    </div>
                    {editingNudge === n.nudge_id ? (
                      <div className="space-y-2 mt-2">
                        <input
                          value={n.subject || ""}
                          onChange={(e) => setNudges(list => list.map(x => x.nudge_id === n.nudge_id ? { ...x, subject: e.target.value } : x))}
                          onBlur={(e) => saveNudge(n, { subject: e.target.value })}
                          placeholder="Subject"
                          className="w-full rounded-lg bg-white border border-slate-200 focus:border-violet-500 focus:ring-4 focus:ring-violet-100 px-3 py-2 text-sm outline-none"
                          data-testid={`nudge-subject-${n.nudge_id}`}
                        />
                        <textarea
                          value={n.body || ""}
                          onChange={(e) => setNudges(list => list.map(x => x.nudge_id === n.nudge_id ? { ...x, body: e.target.value } : x))}
                          onBlur={(e) => saveNudge(n, { body: e.target.value })}
                          rows={6}
                          className="w-full rounded-lg bg-white border border-slate-200 focus:border-violet-500 focus:ring-4 focus:ring-violet-100 px-3 py-2 text-sm font-mono leading-relaxed outline-none"
                          data-testid={`nudge-body-${n.nudge_id}`}
                        />
                      </div>
                    ) : (
                      n.subject && <div className="text-sm text-slate-700 mt-1"><span className="font-semibold">Subject:</span> {n.subject}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {!n.body && (
                      <button onClick={() => draftNudge(n)} disabled={busyNudge === n.nudge_id} className="text-[10px] font-mono font-bold tracking-widest text-fuchsia-600 hover:text-fuchsia-700 inline-flex items-center gap-1 disabled:opacity-50" data-testid={`nudge-draft-${n.nudge_id}`}>
                        {busyNudge === n.nudge_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}AI DRAFT
                      </button>
                    )}
                    {n.body && (
                      <>
                        <button onClick={() => setEditingNudge(editingNudge === n.nudge_id ? null : n.nudge_id)} className="text-[10px] font-mono font-bold tracking-widest text-slate-600 hover:text-slate-900" data-testid={`nudge-edit-${n.nudge_id}`}>{editingNudge === n.nudge_id ? "COLLAPSE" : "EDIT"}</button>
                        <button onClick={() => sendNudge(n)} disabled={busyNudge === n.nudge_id || n.status === "sent"} className="text-[10px] font-mono font-bold tracking-widest text-emerald-600 hover:text-emerald-700 inline-flex items-center gap-1 disabled:opacity-50" data-testid={`nudge-send-${n.nudge_id}`}>
                          {busyNudge === n.nudge_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}SEND
                        </button>
                      </>
                    )}
                    <button onClick={() => cancelNudge(n)} className="text-[10px] font-mono font-bold tracking-widest text-rose-500 hover:text-rose-700" data-testid={`nudge-cancel-${n.nudge_id}`}><Trash2 className="w-3 h-3" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const CERTS = [
  { value: "", label: "Any" },
  { value: "SDVOSB", label: "SDVOSB (Service-Disabled Veteran)" },
  { value: "8A", label: "8(a) Business Development" },
  { value: "WOSB", label: "WOSB (Women-Owned)" },
  { value: "EDWOSB", label: "EDWOSB (Economically Disadvantaged Women-Owned)" },
  { value: "HUBZone", label: "HUBZone" },
  { value: "VOSB", label: "VOSB (Veteran-Owned)" },
];

function SubcontractorSearch({ initial }) {
  const [naics, setNaics] = useState(initial?.naics || "");
  const [keyword, setKeyword] = useState(initial?.keyword || "");
  const [state, setState] = useState(initial?.state || "");
  const [cert, setCert] = useState(initial?.cert || "");
  const [busy, setBusy] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [saved, setSaved] = useState([]);
  const [saveModal, setSaveModal] = useState(false);
  const [saveForm, setSaveForm] = useState({ firm_name: "", contact_name: "", contact_email: "", contact_phone: "", website: "", notes: "" });

  const loadSaved = async () => {
    try { const r = await api.get("/subcontractors/saved"); setSaved(r.data || []); }
    catch {}
  };
  useEffect(() => { loadSaved(); }, []);

  const [results, setResults] = useState(null);   // { total, page, size, firms[] }
  // Sortable client-side view. `name` is the default so LOAD ALL of 500 rows
  // stays scannable. `state` groups by physical HQ, `expiring` surfaces subs
  // whose SAM registration is closest to lapsing (they can't win federal work
  // once expired), and `certs` bubbles the multi-cert generalists to the top.
  const [sortKey, setSortKey] = useState("name");   // name | state | expiring | certs
  const [sortDir, setSortDir] = useState("asc");    // asc | desc

  const sortedFirms = useMemo(() => {
    if (!results?.firms) return [];
    const rows = [...results.firms];
    const cmp = (a, b, key) => {
      if (key === "state") {
        const av = (a.state || "\uffff").toUpperCase();
        const bv = (b.state || "\uffff").toUpperCase();
        return av.localeCompare(bv);
      }
      if (key === "expiring") {
        // Nearer expiration first when asc. Missing dates sink to the bottom.
        const av = a.registration_expiration ? Date.parse(a.registration_expiration) : Infinity;
        const bv = b.registration_expiration ? Date.parse(b.registration_expiration) : Infinity;
        return (av || Infinity) - (bv || Infinity);
      }
      if (key === "certs") {
        return (a.business_types?.length || 0) - (b.business_types?.length || 0);
      }
      // default: legal name alphabetical
      return (a.legal_name || "").localeCompare(b.legal_name || "");
    };
    rows.sort((a, b) => cmp(a, b, sortKey));
    return sortDir === "desc" ? rows.reverse() : rows;
  }, [results, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      // Sensible default direction per column: name/state alpha asc, expiring
      // asc (soonest first), certs desc (most-certified first).
      setSortDir(key === "certs" ? "desc" : "asc");
    }
  };

  // SAM.gov entity search caps 10/page. Load one page, then append additional
  // pages on demand via "Show more" so operators can browse the entire match set
  // without leaving BidForge.
  const PAGE_SIZE = 10;

  const buildParams = (pageNum) => {
    const params = new URLSearchParams();
    if (naics) params.set("naics", naics);
    if (keyword) params.set("keyword", keyword);
    if (state) params.set("state", state);
    if (cert) params.set("certification", cert);
    params.set("size", String(PAGE_SIZE));
    params.set("page", String(pageNum));
    return params;
  };

  // SAM.gov sometimes returns the same firm twice inside a single page response
  // (e.g. two active registrations under the same UEI). Dedupe on UEI (fallback
  // legal_name) so operators never see the same row twice.
  const dedupeFirms = (arr) => {
    const seen = new Set();
    const out = [];
    for (const f of arr || []) {
      const k = f.uei || f.legal_name;
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(f);
    }
    return out;
  };

  const openSearch = async () => {
    setBusy(true);
    setResults(null);
    try {
      const r = await api.get(`/subcontractors/search?${buildParams(0).toString()}`);
      setResults({ ...r.data, firms: dedupeFirms(r.data.firms) });
      toast.success(`Found ${r.data.total} matching firms`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed");
    } finally { setBusy(false); }
  };

  const loadMore = async () => {
    if (!results || loadingMore) return;
    setLoadingMore(true);
    try {
      const nextPage = (results.page ?? 0) + 1;
      const r = await api.get(`/subcontractors/search?${buildParams(nextPage).toString()}`);
      const seen = new Set(results.firms.map((f) => f.uei || f.legal_name));
      const fresh = (r.data.firms || []).filter((f) => {
        const k = f.uei || f.legal_name;
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
      setResults({
        ...r.data,
        firms: [...results.firms, ...fresh],
        page: nextPage,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load next page");
    } finally { setLoadingMore(false); }
  };

  // Walk the paginator to the end (or a safety cap of 500 firms). Fires pages
  // sequentially, not in parallel — SAM.gov throttles hard on the entity API
  // and we don't want to burn the workspace's quota on one query.
  const MAX_LOAD_ALL = 500;
  const loadAll = async () => {
    if (!results || loadingMore) return;
    setLoadingMore(true);
    try {
      let page = results.page ?? 0;
      let firms = [...results.firms];
      const seen = new Set(firms.map((f) => f.uei || f.legal_name));
      const total = results.total || 0;
      const target = Math.min(total, MAX_LOAD_ALL);
      let guard = 60; // absolute safety on pages fetched (600 firms max)
      while (firms.length < target && guard > 0) {
        page += 1;
        guard -= 1;
        const r = await api.get(`/subcontractors/search?${buildParams(page).toString()}`);
        const fresh = [];
        for (const f of r.data.firms || []) {
          const k = f.uei || f.legal_name;
          if (seen.has(k)) continue;
          seen.add(k);
          fresh.push(f);
        }
        if (fresh.length === 0) break;
        firms = [...firms, ...fresh];
      }
      setResults((prev) => (prev ? { ...prev, firms, page } : prev));
      if (firms.length >= MAX_LOAD_ALL && total > MAX_LOAD_ALL) {
        toast.message(`Loaded ${firms.length} of ${total.toLocaleString()} — capped at ${MAX_LOAD_ALL} so we don't hammer SAM.gov. Narrow the filters if you need to go deeper.`);
      } else {
        toast.success(`All ${firms.length.toLocaleString()} firms loaded`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't finish loading all pages");
    } finally { setLoadingMore(false); }
  };

  // Auto-fire the search on mount when we were deep-linked in with filters
  // (e.g. Opportunity Detail → "Find subs for NAICS X"). Prevents the operator
  // from having to re-type what we already know.
  useEffect(() => {
    if (initial?.autoFire && (initial.naics || initial.keyword || initial.state)) {
      openSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bookmarkFirm = async (firm) => {
    try {
      const r = await api.post("/subcontractors/saved", {
        firm_name: firm.legal_name || firm.dba_name || "Unknown firm",
        contact_name: null,
        contact_email: null,
        contact_phone: null,
        website: firm.entity_url,
        notes: `UEI:${firm.uei || "?"} · CAGE:${firm.cage_code || "?"} · ${firm.city || ""}, ${firm.state || ""}`,
        naics: firm.primary_naics || naics || null,
        trade: keyword || null,
        state: firm.state || state || null,
        certification: cert || null,
        source_url: firm.entity_url,
      });
      if (r.data.already_existed) toast.info(`${firm.legal_name} was already saved`);
      else toast.success(`Saved ${firm.legal_name}`);
      loadSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const openSaveModal = () => {
    setSaveForm({ firm_name: "", contact_name: "", contact_email: "", contact_phone: "", website: "", notes: "" });
    setSaveModal(true);
  };

  const submitSave = async () => {
    if (!saveForm.firm_name.trim()) { toast.error("Firm name is required"); return; }
    try {
      const r = await api.post("/subcontractors/saved", {
        ...saveForm,
        naics: naics || null,
        trade: keyword || null,
        state: state || null,
        certification: cert || null,
        source_url: null,
      });
      if (r.data.already_existed) toast.info(`${saveForm.firm_name} was already saved`);
      else toast.success(`Saved ${saveForm.firm_name}`);
      setSaveModal(false);
      loadSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Remove ${name} from saved subs?`)) return;
    try { await api.delete(`/subcontractors/saved/${id}`); toast.success("Removed"); loadSaved(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6" data-testid="subs-search">
      <div className="lg:col-span-2 space-y-6">
        <div className="rounded-2xl bg-gradient-to-br from-emerald-50 via-white to-teal-50 border border-emerald-200 p-5 flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 text-white flex items-center justify-center shrink-0">
            <Handshake className="w-5 h-5" />
          </div>
          <div className="text-sm text-slate-700 leading-relaxed">
            <div className="font-display font-bold text-slate-900 mb-1">Find subcontractors — results stay in BidForge</div>
            Search real firms (SAM.gov-registered small businesses) by NAICS, trade, state, and set-aside program. Every result stays inside the app — bookmark firms straight from the results table, and they land in your workspace for the next similar bid.
          </div>
        </div>

        <div className="rounded-2xl bg-white border border-slate-200 p-5">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-2">NAICS CODE</div>
              <input value={naics} onChange={(e) => setNaics(e.target.value)} placeholder="238160" className="w-full rounded-xl bg-white border border-slate-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 px-3 py-2 text-sm outline-none" data-testid="subs-naics" />
            </div>
            <div>
              <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-2">TRADE / KEYWORD</div>
              <input value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder="roofing, mod bit, hvac…" className="w-full rounded-xl bg-white border border-slate-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 px-3 py-2 text-sm outline-none" data-testid="subs-keyword" />
            </div>
            <div>
              <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-2">STATE</div>
              <input value={state} onChange={(e) => setState(e.target.value.toUpperCase().slice(0, 2))} placeholder="TX" className="w-full rounded-xl bg-white border border-slate-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 px-3 py-2 text-sm outline-none" data-testid="subs-state" />
            </div>
            <div>
              <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-2">SET-ASIDE / CERT</div>
              <select value={cert} onChange={(e) => setCert(e.target.value)} className="w-full rounded-xl bg-white border border-slate-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 px-3 py-2 text-sm outline-none" data-testid="subs-cert">
                {CERTS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <button onClick={openSearch} disabled={busy} className="bf-pill px-6 py-2.5 text-xs tracking-widest uppercase font-display font-bold inline-flex items-center gap-2 disabled:opacity-50" data-testid="subs-search-btn">
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" />SEARCHING</> : <><Search className="w-4 h-4" />SEARCH FIRMS</>}
            </button>
            <button onClick={openSaveModal} className="px-5 py-2.5 rounded-full text-xs tracking-widest uppercase font-display font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 inline-flex items-center gap-2" data-testid="subs-save-btn">
              <Bookmark className="w-4 h-4" />BOOKMARK MANUALLY
            </button>
          </div>
        </div>

        {results && (
          <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden" data-testid="subs-results-panel">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
              <div className="text-[10px] font-mono font-bold tracking-widest text-emerald-700 flex items-center gap-2">
                <Search className="w-3.5 h-3.5" />
                {results.total.toLocaleString()} FIRM{results.total === 1 ? "" : "S"} MATCHED
              </div>
              <div className="text-[10px] font-mono text-slate-400" data-testid="subs-results-count">
                SHOWING {results.firms.length} OF {results.total.toLocaleString()}
              </div>
            </div>

            {/* Sort toolbar — client-side sort over whatever is currently
                loaded. Tap the same key again to flip direction. Once you've
                run LOAD ALL you can slice 500 rows by state, expiration, or
                certs without another API call. */}
            {results.firms.length > 1 && (
              <div className="px-4 py-2 border-b border-slate-100 bg-slate-50/50 flex items-center gap-2 flex-wrap" data-testid="subs-sort-toolbar">
                <span className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mr-1">SORT</span>
                {[
                  { key: "name",     label: "Name" },
                  { key: "state",    label: "State" },
                  { key: "expiring", label: "Expiration" },
                  { key: "certs",    label: "Certs" },
                ].map((opt) => {
                  const active = sortKey === opt.key;
                  const Icon = active ? (sortDir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
                  return (
                    <button
                      key={opt.key}
                      onClick={() => toggleSort(opt.key)}
                      className={`text-[10px] font-mono font-bold tracking-widest px-2.5 py-1 rounded-full inline-flex items-center gap-1 transition-colors ${
                        active
                          ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                          : "bg-white text-slate-500 border border-slate-200 hover:border-emerald-200 hover:text-emerald-700"
                      }`}
                      data-testid={`subs-sort-${opt.key}`}
                      title={active ? `Sorted ${sortDir}ending — tap to flip` : `Sort by ${opt.label.toLowerCase()}`}
                    >
                      <Icon className="w-3 h-3" />
                      {opt.label.toUpperCase()}
                    </button>
                  );
                })}
              </div>
            )}

            <div className="divide-y divide-slate-100">
              {results.firms.length === 0 && (
                <div className="p-6 text-sm text-slate-500" data-testid="subs-results-empty">
                  No firms matched. Try widening the filters (drop the state, or remove the certification).
                </div>
              )}
              {sortedFirms.map((f) => (
                <div key={f.uei || f.legal_name} className="px-4 py-3 flex items-start gap-3" data-testid={`subs-result-${f.uei || f.legal_name}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-semibold text-slate-900 truncate">{f.legal_name}</div>
                    {f.dba_name && <div className="text-xs text-slate-500 italic">dba {f.dba_name}</div>}
                    <div className="text-[11px] font-mono text-slate-500 mt-1 flex flex-wrap gap-x-3 gap-y-1">
                      <span>{f.city || "?"}, {f.state || "?"}</span>
                      {f.primary_naics && <span className="text-indigo-700">NAICS {f.primary_naics}</span>}
                      {f.cage_code && <span>CAGE {f.cage_code}</span>}
                      {f.uei && <span className="text-slate-400">UEI {f.uei}</span>}
                      {f.registration_expiration && <span className="text-slate-400">EXP {f.registration_expiration}</span>}
                    </div>
                    {(f.business_types || []).length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {f.business_types.slice(0, 4).map((bt, i) => (
                          <span key={i} className="text-[10px] font-mono uppercase tracking-widest text-emerald-700 border border-emerald-200 bg-emerald-50 rounded px-1.5 py-0.5" title={bt.desc}>{bt.desc || bt.code}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="shrink-0">
                    <button
                      onClick={() => bookmarkFirm(f)}
                      className="text-[10px] font-mono font-bold uppercase tracking-widest text-emerald-700 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 px-2 py-1 rounded inline-flex items-center gap-1"
                      data-testid={`subs-result-save-${f.uei || f.legal_name}`}
                    >
                      <Bookmark className="w-3 h-3" />SAVE
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {results.firms.length < results.total && (
              <div className="border-t border-slate-100 p-3 flex justify-center gap-3 flex-wrap">
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="text-xs font-mono font-bold tracking-widest text-emerald-700 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 px-4 py-2 rounded-full inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="subs-load-more"
                >
                  {loadingMore ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />LOADING…</> : <><ChevronDown className="w-3.5 h-3.5" />SHOW NEXT {Math.min(PAGE_SIZE, results.total - results.firms.length)} FIRMS</>}
                </button>
                <button
                  onClick={loadAll}
                  disabled={loadingMore}
                  title="Walk every page in one shot (capped at 500 to protect SAM.gov quota)"
                  className="text-xs font-mono font-bold tracking-widest text-violet-700 border border-violet-200 bg-violet-50 hover:bg-violet-100 px-4 py-2 rounded-full inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="subs-load-all"
                >
                  <Zap className="w-3.5 h-3.5" />LOAD ALL {Math.min(results.total, 500).toLocaleString()}
                </button>
              </div>
            )}
          </div>
        )}

        <div className="rounded-2xl bg-white border border-slate-200 p-5 text-xs text-slate-600 leading-relaxed">
          <div className="font-display font-bold text-slate-800 mb-2">Tips for shingles-to-shingles matches</div>
          <ul className="list-disc list-inside space-y-1">
            <li>Enter the same NAICS you're bidding to keep the trade tight (e.g. <b>238160</b> for roofing).</li>
            <li>Use a specific keyword like <b>"mod bit"</b>, <b>"metal roof"</b>, or <b>"HVAC replacement"</b> rather than a broad term.</li>
            <li>Filter by state to reduce mobilization cost surprises.</li>
            <li>Match your prime's set-aside (SDVOSB/HUBZone/8(a)) so the sub qualifies under the same rules.</li>
          </ul>
        </div>
      </div>

      <div className="lg:col-span-1">
        <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden sticky top-4" data-testid="subs-saved-panel">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <div className="text-[10px] font-mono font-bold tracking-widest text-emerald-700 flex items-center gap-1.5">
              <Bookmark className="w-3.5 h-3.5" />SAVED SUBS ({saved.length})
            </div>
          </div>
          {saved.length === 0 ? (
            <div className="p-6 text-center text-xs text-slate-500" data-testid="subs-saved-empty">
              No bookmarks yet. Run an SBA search, then hit <b>BOOKMARK A SUB</b> to save firms you'd tap on your next bid.
            </div>
          ) : (
            <div className="max-h-[520px] overflow-y-auto divide-y divide-slate-100">
              {saved.map((s) => (
                <div key={s.sub_id} className="p-4 hover:bg-emerald-50/40 transition-colors" data-testid={`subs-saved-row-${s.sub_id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-slate-900 text-sm truncate">{s.firm_name}</div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {s.naics && <span className="text-[9px] font-mono font-bold tracking-wider text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-full px-1.5 py-0.5">NAICS {s.naics}</span>}
                        {s.trade && <span className="text-[9px] font-mono font-bold tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-1.5 py-0.5">{s.trade}</span>}
                        {s.state && <span className="text-[9px] font-mono font-bold tracking-wider text-sky-700 bg-sky-50 border border-sky-100 rounded-full px-1.5 py-0.5">{s.state}</span>}
                        {s.certification && <span className="text-[9px] font-mono font-bold tracking-wider text-amber-700 bg-amber-50 border border-amber-100 rounded-full px-1.5 py-0.5">{s.certification}</span>}
                      </div>
                      {(s.contact_name || s.contact_email || s.contact_phone) && (
                        <div className="text-[11px] text-slate-600 mt-2 leading-snug">
                          {s.contact_name && <div className="truncate">{s.contact_name}</div>}
                          {s.contact_email && <a href={`mailto:${s.contact_email}`} className="text-emerald-700 hover:text-emerald-800 truncate block">{s.contact_email}</a>}
                          {s.contact_phone && <div className="font-mono text-[10px]">{s.contact_phone}</div>}
                        </div>
                      )}
                      {s.notes && <div className="text-[11px] text-slate-500 mt-2 line-clamp-2 italic">{s.notes}</div>}
                    </div>
                    <button onClick={() => remove(s.sub_id, s.firm_name)} className="text-slate-300 hover:text-rose-600 shrink-0" data-testid={`subs-saved-remove-${s.sub_id}`} aria-label="Remove">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {saveModal && (
        <div className="fixed inset-0 z-[80] bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="subs-save-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 relative">
            <button onClick={() => setSaveModal(false)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-700" data-testid="subs-save-close" aria-label="Close">
              <X className="w-5 h-5" />
            </button>
            <div className="bf-eyebrow inline-block text-emerald-600 mb-3">BOOKMARK A SUB</div>
            <div className="font-display font-black text-2xl text-slate-900 mb-1">Save this subcontractor</div>
            <div className="text-sm text-slate-500 mb-5">Bookmarks are workspace-scoped — every teammate sees the same roster.</div>
            <div className="space-y-3">
              <div>
                <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-1">FIRM NAME *</div>
                <input value={saveForm.firm_name} onChange={(e) => setSaveForm({ ...saveForm, firm_name: e.target.value })} placeholder="e.g. Acme Roofing LLC" className="w-full rounded-xl bg-white border border-slate-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 px-3 py-2 text-sm outline-none" data-testid="subs-save-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-1">CONTACT NAME</div>
                  <input value={saveForm.contact_name} onChange={(e) => setSaveForm({ ...saveForm, contact_name: e.target.value })} className="w-full rounded-xl bg-white border border-slate-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 px-3 py-2 text-sm outline-none" data-testid="subs-save-contact" />
                </div>
                <div>
                  <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-1">PHONE</div>
                  <input value={saveForm.contact_phone} onChange={(e) => setSaveForm({ ...saveForm, contact_phone: e.target.value })} className="w-full rounded-xl bg-white border border-slate-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 px-3 py-2 text-sm outline-none" data-testid="subs-save-phone" />
                </div>
              </div>
              <div>
                <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-1">EMAIL</div>
                <input value={saveForm.contact_email} onChange={(e) => setSaveForm({ ...saveForm, contact_email: e.target.value })} className="w-full rounded-xl bg-white border border-slate-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 px-3 py-2 text-sm outline-none" data-testid="subs-save-email" />
              </div>
              <div>
                <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-1">WEBSITE</div>
                <input value={saveForm.website} onChange={(e) => setSaveForm({ ...saveForm, website: e.target.value })} placeholder="https://…" className="w-full rounded-xl bg-white border border-slate-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 px-3 py-2 text-sm outline-none" data-testid="subs-save-website" />
              </div>
              <div>
                <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-1">NOTES</div>
                <textarea value={saveForm.notes} onChange={(e) => setSaveForm({ ...saveForm, notes: e.target.value })} rows={3} placeholder="Pricing, past performance, quirks…" className="w-full rounded-xl bg-white border border-slate-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 px-3 py-2 text-sm outline-none" data-testid="subs-save-notes" />
              </div>
              <div className="text-[11px] text-slate-500">
                Auto-tagged with your current search: NAICS <b>{naics || "—"}</b> · trade <b>{keyword || "—"}</b> · state <b>{state || "—"}</b> · cert <b>{cert || "—"}</b>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setSaveModal(false)} className="bf-pill-ghost px-5 py-2.5 text-xs tracking-widest uppercase font-display font-bold" data-testid="subs-save-cancel">CANCEL</button>
              <button onClick={submitSave} className="bf-pill px-5 py-2.5 text-xs tracking-widest uppercase font-display font-bold inline-flex items-center gap-2" data-testid="subs-save-submit">
                <Bookmark className="w-4 h-4" />SAVE
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Connect() {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") === "subs" || searchParams.get("tab") === "pipeline" ? searchParams.get("tab") : "search";
  const [tab, setTab] = useState(initialTab);
  const [f, setF] = useState({
    naics: searchParams.get("naics") || "",
    state: (searchParams.get("state") || "").toUpperCase().slice(0, 2),
    keyword: searchParams.get("keyword") || "",
    days_back: Number(searchParams.get("days_back")) || 90,
  });
  const [awards, setAwards] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [draftOpen, setDraftOpen] = useState(null);
  const [draft, setDraft] = useState({ subject: "", body: "", loading: false });

  const loadContacts = async () => {
    try { const r = await api.get("/connect/contacts"); setContacts(r.data || []); }
    catch {}
  };
  useEffect(() => { loadContacts(); }, []);

  const searchAwards = async () => {
    setBusy(true);
    try {
      const p = new URLSearchParams();
      Object.entries(f).forEach(([k, v]) => { if (v !== "" && v !== null) p.append(k, v); });
      const r = await api.get(`/connect/awards?${p.toString()}`);
      setAwards(r.data || []);
      toast.success(`${(r.data || []).length} recent awards from USAspending.gov`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "USAspending query failed");
    } finally { setBusy(false); }
  };

  // Auto-run search when deep-linked from Opportunity Detail (?auto=1&naics=...)
  useEffect(() => {
    if (searchParams.get("auto") === "1" && (f.naics || f.keyword)) {
      searchAwards();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const savePrime = async (a) => {
    setSavingId(a.award_id);
    try {
      await api.post("/connect/contacts", {
        prime_name: a.prime_name,
        prime_uei: a.prime_uei,
        agency: a.agency,
        award_amount: a.amount,
        award_date: a.start_date,
        naics: a.naics,
        place_of_performance: [a.pop_city, a.pop_state].filter(Boolean).join(", "),
        source_award_id: a.award_id,
        award_description: a.description,
        status: "new",
      });
      setAwards(list => list.map(x => x.award_id === a.award_id ? { ...x, saved: true } : x));
      loadContacts();
      toast.success(`Added ${a.prime_name} — SAM.gov POC pulled if available`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingId(null); }
  };

  const updateStatus = async (c, status) => {
    try {
      const r = await api.patch(`/connect/contacts/${c.contact_id}`, { status });
      setContacts(list => list.map(x => x.contact_id === c.contact_id ? { ...x, ...r.data } : x));
      if (status === "contacted") toast.success("Cadence armed — check the CADENCE panel for follow-ups");
    } catch { toast.error("Update failed"); }
  };
  const replaceContact = (updated) => setContacts(list => list.map(x => x.contact_id === updated.contact_id ? { ...x, ...updated } : x));
  const removeContact = async (c) => {
    if (!window.confirm(`Remove ${c.prime_name} from teaming pipeline?`)) return;
    try {
      await api.delete(`/connect/contacts/${c.contact_id}`);
      setContacts(list => list.filter(x => x.contact_id !== c.contact_id));
    } catch { toast.error("Delete failed"); }
  };
  const exportCsv = () => {
    const base = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem("token") || "";
    if (!token) {
      toast.error("You need to be signed in to export.");
      return;
    }
    // window.open() cannot carry the Authorization header, so we pass the
    // JWT via the ?auth= query param (same pattern as proposal / branding /
    // submittals downloads).  Backend accepts either header OR ?auth=.
    window.open(`${base}/api/connect/contacts.csv?auth=${encodeURIComponent(token)}`, "_blank");
  };
  const draftEmail = async (prime) => {
    setDraftOpen(prime); setDraft({ subject: "", body: "", loading: true });
    try {
      const r = await api.post("/connect/draft-outreach", {
        prime_name: prime.prime_name,
        agency: prime.agency,
        award_description: prime.award_description || prime.description,
        naics: prime.naics,
        tone: "professional",
      });
      setDraft({ subject: r.data.subject || "", body: r.data.body || r.data.raw || "", loading: false });
    } catch (e) {
      setDraft({ subject: "", body: "", loading: false });
      toast.error(e?.response?.data?.detail || "Draft failed");
    }
  };
  const copyDraft = () => {
    const txt = `Subject: ${draft.subject}\n\n${draft.body}`;
    navigator.clipboard.writeText(txt); toast.success("Copied to clipboard");
  };

  return (
    <div className="p-8 stagger-in" data-testid="connect-page">
      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="bf-eyebrow inline-block text-emerald-600 mb-3">CONNECT</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight text-slate-900">
            Team with <span className="bf-grad-text">primes</span> who just won.
          </h1>
          <p className="text-slate-500 mt-3 max-w-2xl">USAspending.gov awards + SAM.gov POC enrichment + a 3-touch AI follow-up cadence that arms itself the moment you mark a prime "contacted".</p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <PageTour
            storageKey="bf_connect_tour_v1"
            eyebrow="CONNECT"
            title="Building your teaming pipeline"
            Icon={Handshake}
            tone="emerald"
            suppressAutoOpen={searchParams.get("auto") === "1"}
            steps={[
              { Icon: Search,     tone: "indigo",  title: "1. Find primes who just won",     body: "FIND PRIMES searches USAspending.gov for recent federal awards in your NAICS. Every row shows the winner, contract value, and place of performance so you know who to team with." },
              { Icon: Building2,  tone: "emerald", title: "2. Find subs (SBA certifications)",body: "FIND SUBS opens the official SBA Certifications database in a new tab with your NAICS + trade + state + set-aside pre-filtered. Bookmark subs you like — they save into your workspace." },
              { Icon: Users,      tone: "violet",  title: "3. Add to your pipeline",         body: "ADD flips a prime into your Teaming Pipeline (status = new). Every add auto-enriches from SAM.gov (POC name, email, phone, address) so you have real contact info in 2 seconds." },
              { Icon: Sparkles,   tone: "fuchsia", title: "4. AI-draft outreach",            body: "DRAFT EMAIL uses Claude Sonnet + your Company profile + Branding to write a personalized intro email in seconds. Costs 1 credit. Copy or open in your mail app." },
              { Icon: Send,       tone: "amber",   title: "5. Auto 3-touch cadence",         body: "Mark a prime 'contacted' and BidForge auto-arms 3 follow-up nudges at +3, +7, +14 days. Draft each with AI, send via Resend or your mail client. No one falls through the cracks." },
            ]}
          />
          <button onClick={exportCsv} className="bf-pill-ghost px-5 py-2.5 text-xs tracking-widest uppercase font-display font-bold inline-flex items-center gap-2" data-testid="connect-export-csv">
            <Download className="w-4 h-4" />Export CSV
          </button>
        </div>
      </div>

      <div className="flex border-b border-slate-200 mb-6">
        <button onClick={() => setTab("search")} className={`px-5 py-3 text-xs font-mono font-bold tracking-widest transition-colors ${tab === "search" ? "text-violet-600 border-b-2 border-violet-500 -mb-px" : "text-slate-500 hover:text-slate-900"}`} data-testid="connect-tab-search">FIND PRIMES</button>
        <button onClick={() => setTab("subs")} className={`px-5 py-3 text-xs font-mono font-bold tracking-widest transition-colors ${tab === "subs" ? "text-violet-600 border-b-2 border-violet-500 -mb-px" : "text-slate-500 hover:text-slate-900"}`} data-testid="connect-tab-subs">FIND SUBS</button>
        <button onClick={() => setTab("pipeline")} className={`px-5 py-3 text-xs font-mono font-bold tracking-widest transition-colors inline-flex items-center gap-2 ${tab === "pipeline" ? "text-violet-600 border-b-2 border-violet-500 -mb-px" : "text-slate-500 hover:text-slate-900"}`} data-testid="connect-tab-pipeline">
          TEAMING PIPELINE {contacts.length > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700">{contacts.length}</span>}
        </button>
      </div>

      {tab === "subs" && (
        <SubcontractorSearch
          initial={{
            naics: searchParams.get("naics") || "",
            keyword: searchParams.get("keyword") || "",
            state: (searchParams.get("state") || "").toUpperCase().slice(0, 2),
            cert: searchParams.get("cert") || "",
            autoFire: searchParams.get("auto") === "1",
          }}
        />
      )}

      {tab === "search" && (
        <>
          <div className="rounded-2xl bg-white border border-slate-200 p-5 mb-6" data-testid="connect-filters">
            <div className="grid md:grid-cols-4 gap-4">
              <div>
                <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-2">KEYWORD</div>
                <input value={f.keyword} onChange={(e) => setF({ ...f, keyword: e.target.value })} onKeyDown={(e) => e.key === "Enter" && searchAwards()} placeholder="e.g. roofing" className="w-full rounded-xl bg-white border border-slate-200 focus:border-violet-500 focus:ring-4 focus:ring-violet-100 px-3 py-2 text-sm outline-none" data-testid="connect-keyword" />
              </div>
              <div>
                <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-2">NAICS</div>
                <input value={f.naics} onChange={(e) => setF({ ...f, naics: e.target.value })} placeholder="238160" className="w-full rounded-xl bg-white border border-slate-200 focus:border-violet-500 focus:ring-4 focus:ring-violet-100 px-3 py-2 text-sm outline-none" data-testid="connect-naics" />
              </div>
              <div>
                <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-2">STATE (POP)</div>
                <input value={f.state} onChange={(e) => setF({ ...f, state: e.target.value.toUpperCase().slice(0, 2) })} placeholder="TX" className="w-full rounded-xl bg-white border border-slate-200 focus:border-violet-500 focus:ring-4 focus:ring-violet-100 px-3 py-2 text-sm outline-none" data-testid="connect-state" />
              </div>
              <div>
                <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-2">AWARDED (DAYS BACK)</div>
                <select value={f.days_back} onChange={(e) => setF({ ...f, days_back: Number(e.target.value) })} className="w-full rounded-xl bg-white border border-slate-200 focus:border-violet-500 focus:ring-4 focus:ring-violet-100 px-3 py-2 text-sm outline-none" data-testid="connect-days">
                  {[30, 60, 90, 180, 365].map(d => <option key={d} value={d}>{d} days</option>)}
                </select>
              </div>
            </div>
            <div className="mt-4">
              <button onClick={searchAwards} disabled={busy} className="bf-pill px-6 py-2.5 text-xs tracking-widest uppercase font-display font-bold inline-flex items-center gap-2 disabled:opacity-50" data-testid="connect-search">
                {busy ? <><Loader2 className="w-4 h-4 animate-spin" />SEARCHING</> : <><Search className="w-4 h-4" />FIND WINNERS</>}
              </button>
            </div>
          </div>

          <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
            {busy && awards.length === 0 && (
              <div className="p-12 text-center text-slate-500 text-sm flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Searching USAspending.gov…</div>
            )}
            {!busy && awards.length === 0 && (
              <div className="p-12 text-center text-slate-500 text-sm">
                <Handshake className="w-8 h-8 mx-auto mb-3 text-slate-300" />
                Search recent federal awards to build your teaming pipeline.
              </div>
            )}
            {awards.map((a) => (
              <div key={a.award_id} className="p-5 border-b border-slate-100 last:border-b-0 hover:bg-emerald-50/40 transition-colors" data-testid={`award-row-${a.award_id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900">{a.prime_name || "(Unknown recipient)"}</div>
                    <div className="text-[11px] font-mono text-slate-500 mt-1 truncate">{a.agency || "—"}{a.sub_agency ? ` · ${a.sub_agency}` : ""}</div>
                    {a.description && <div className="text-sm text-slate-600 mt-2 line-clamp-2">{a.description}</div>}
                    <div className="mt-2 flex flex-wrap gap-2">
                      {a.naics && <span className="text-[10px] font-mono font-bold tracking-wider text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-full px-2 py-0.5">NAICS {a.naics}</span>}
                      {a.pop_state && <span className="text-[10px] font-mono font-bold tracking-wider text-sky-700 bg-sky-50 border border-sky-100 rounded-full px-2 py-0.5">{a.pop_city || ""}{a.pop_city && a.pop_state ? ", " : ""}{a.pop_state}</span>}
                      {a.start_date && <span className="text-[10px] font-mono font-bold tracking-wider text-slate-600 bg-slate-50 border border-slate-200 rounded-full px-2 py-0.5">Awarded {new Date(a.start_date).toLocaleDateString()}</span>}
                    </div>
                  </div>
                  <div className="text-right shrink-0 flex flex-col items-end gap-2">
                    <div className="font-display font-black text-2xl text-emerald-600">{money(a.amount)}</div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => draftEmail(a)} className="text-[10px] font-mono font-bold tracking-widest text-fuchsia-600 hover:text-fuchsia-700 inline-flex items-center gap-1" data-testid={`draft-${a.award_id}`}><Sparkles className="w-3 h-3" />DRAFT EMAIL</button>
                      {a.saved ? (
                        <span className="text-[10px] font-mono font-bold tracking-widest text-emerald-600 inline-flex items-center gap-1"><Handshake className="w-3 h-3" />IN PIPELINE</span>
                      ) : (
                        <button onClick={() => savePrime(a)} disabled={savingId === a.award_id} className="text-[10px] font-mono font-bold tracking-widest text-violet-600 hover:text-violet-700 inline-flex items-center gap-1 disabled:opacity-50" data-testid={`add-${a.award_id}`}>
                          {savingId === a.award_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}ADD
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {tab === "pipeline" && (
        <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden" data-testid="teaming-pipeline">
          {contacts.length === 0 && (
            <div className="p-12 text-center text-slate-500 text-sm">
              <Handshake className="w-8 h-8 mx-auto mb-3 text-slate-300" />
              Your teaming pipeline is empty. Go to <span className="text-violet-600 font-semibold">Find Primes</span> to add contacts.
            </div>
          )}
          {contacts.map((c) => (
            <ContactRow
              key={c.contact_id}
              c={c}
              onStatus={updateStatus}
              onDelete={removeContact}
              onEnrich={replaceContact}
              onDraft={draftEmail}
            />
          ))}
        </div>
      )}

      {draftOpen && (
        <div className="fixed inset-0 z-[80] bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="draft-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-6 relative">
            <button onClick={() => setDraftOpen(null)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-700" data-testid="draft-close" aria-label="Close">
              <X className="w-5 h-5" />
            </button>
            <div className="bf-eyebrow inline-block text-fuchsia-600 mb-3">AI DRAFT</div>
            <div className="font-display font-black text-2xl text-slate-900 mb-1">Outreach to {draftOpen.prime_name}</div>
            <div className="text-sm text-slate-500 mb-6">Model uses your company profile and branding — review before sending.</div>
            {draft.loading ? (
              <div className="py-16 text-center text-slate-500 text-sm flex items-center justify-center gap-2"><Loader2 className="w-5 h-5 animate-spin" />Drafting with Claude…</div>
            ) : (
              <>
                <div className="mb-3">
                  <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-2">SUBJECT</div>
                  <input value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} className="w-full rounded-xl bg-white border border-slate-200 focus:border-violet-500 focus:ring-4 focus:ring-violet-100 px-3 py-2 text-sm outline-none" data-testid="draft-subject" />
                </div>
                <div className="mb-4">
                  <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-2">BODY</div>
                  <textarea value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} rows={12} className="w-full rounded-xl bg-white border border-slate-200 focus:border-violet-500 focus:ring-4 focus:ring-violet-100 px-3 py-2 text-sm outline-none font-mono leading-relaxed" data-testid="draft-body" />
                </div>
                <div className="flex justify-end gap-3">
                  <button onClick={copyDraft} className="bf-pill-ghost px-5 py-2.5 text-xs tracking-widest uppercase font-display font-bold inline-flex items-center gap-2" data-testid="draft-copy"><Copy className="w-4 h-4" />COPY</button>
                  <a href={`mailto:?subject=${encodeURIComponent(draft.subject)}&body=${encodeURIComponent(draft.body)}`} className="bf-pill px-5 py-2.5 text-xs tracking-widest uppercase font-display font-bold inline-flex items-center gap-2" data-testid="draft-mailto"><Mail className="w-4 h-4" />OPEN IN EMAIL</a>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
